import { useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import useViewportOverlap from '../../../hooks/useViewportOverlap';
import FootBand from '../FootBand/FootBand';
import useRestingOverlap, {
  useTypingFocus,
  isTypingTarget,
} from '../../../hooks/useRestingOverlap';
import styles from './SheetOverlay.module.css';

/**
 * The new experience's bottom sheet (signup D1; Drop 7 DETAIL §8 + appendix
 * "overlays", spec frames E1–E8). Every editor on the Detail and Profile pages
 * opens in it: a dimmed page, a #F7F7F5 sheet with a 22px top radius, a title,
 * an optional line under it, white rows, then the actions. A sheet rather
 * than a screen, because you came from one field and go straight back to it.
 *
 * Motion (§9): the scrim only fades (220 in / 180 out); the sheet carries the
 * movement (300ms in, 200ms out — leaving is a release, not a decision) and
 * stays mounted through its exit (AnimatePresence).
 *
 * Bottom: the sheet runs to the page's foot, UNDER Safari's glass toolbar
 * (user rule, 2026-09-28: the bottom URL area is always transparent), and its
 * bottom padding grows by whatever covers the foot — measured live — so the
 * buttons stay clear above it. With the keyboard up (Safari drops its bar)
 * the sheet sits on top of the keyboard instead.
 *
 * The host owns position: the overlay is absolute and fills its nearest
 * positioned ancestor (the page root), never fixed (iOS 26 clips fixed content
 * at Safari's controls).
 *
 * @param {object} p
 * @param {boolean} p.open
 * @param {string} p.title
 * @param {React.ReactNode} [p.description] - the grey line under the title
 * @param {Function} p.onClose - tap on the dimmed page
 * @param {React.ReactNode} p.children - rows and actions
 * @param {boolean} [p.soft] - the SOFT sheet (SIGNUP_Anon §6e confirm-number sheet,
 *   exchange-overlay .cn-soft, 2 Oct 2026): no dim and no blur — the screen stays
 *   bright; the sheet lifts on a soft shadow and rises on a longer eased curve with
 *   a slight fade. The page under it still takes the tap that closes it.
 * @param {boolean} [p.bigTitle] - the share sheet's 27px title (share-sheet rules, rule 9)
 * @param {boolean} [p.keepFocus] - a field inside keeps focus (and the keyboard
 *   stays up) through taps on the dimmed page, the keyboard fill and the sheet's
 *   own padding: mousedown is prevented there (the keyboard spike's rule 1; the
 *   guest sheets, signup E3a). Fields and the sheet's buttons decide for themselves.
 * @param {boolean} [p.animateHeight] - the sheet's height changes ride the
 *   sheet's own motion (SHEET_IN), in one movement, when its content grows or
 *   shrinks inside one open sheet (Mark's bar, 2026-10-19: "gracefully … expand")
 * @param {Function} [p.onExited] - the exit has finished playing (AnimatePresence)
 * @param {boolean} [p.capTop] - a long list: the sheet's top never rises above 25%
 *   of the screen and the list scrolls inside (PROFILE §5, the Add-a-contact type
 *   list, CD Drop 17); the tap on the dimmed page above stays the way out
 * @param {boolean} [p.quiet] - the WAITING sheet (SIGNUP_Profile §3.3, `.sf-sheet.quiet`):
 *   soft (no dim), a third of the shadow, the title 19px/400 in secondary, centred —
 *   "dressed down on purpose: a natural step, not a problem to resolve". Its way
 *   out (the bare X) is the host's, as a child — the sheet positions children.
 * @param {boolean} [p.centred] - the title centred (the exchange Share sheet:
 *   `.sf-sheet` align-items:center, SIGNUP_Profile §2). Opt-in; off, nothing changes.
 * @param {React.ReactNode} [p.head] - above the title: the share sheet's photo of
 *   the person it's about (PXP spec §3b H5; SheetPhotoHead)
 *
 * Safari's bottom bar: while the sheet is up — from opening until its exit has
 * FINISHED — it holds the FootBand in its own colour, replacing whatever band
 * the page has (the bar is the surface on the bottom edge; Mark 2026-10-19).
 *
 * Fields: the first tap on a text field in the sheet focuses it from code with
 * `preventScroll`, inside the tap, instead of the native focus — iOS's native
 * focus scrolls the page to the field, which jumped the whole screen under the
 * sheet (v7976/v7977 recordings: with it, the page never moves). A field already
 * focused is left native, so later taps still place the caret.
 */
const SheetOverlay = ({
  open,
  title,
  description,
  onClose,
  children,
  soft = false,
  bigTitle,
  keepFocus = false,
  animateHeight = false,
  onExited,
  capTop = false,
  quiet = false,
  centred = false,
  head = null,
}) => {
  // Up from opening until the exit has finished — the band's lifetime
  const [shown, setShown] = useState(open);
  if (open && !shown) setShown(true);
  return (
    <>
      {shown ? <FootBand colour="var(--card-bg)" zIndex={1000} layer={1} /> : null}
      <AnimatePresence
        onExitComplete={() => {
          setShown(false);
          onExited?.();
        }}
      >
        {open ? (
          <SheetBody
            title={title}
            description={description}
            onClose={onClose}
            soft={soft || quiet}
            quiet={quiet}
            centred={centred}
            bigTitle={bigTitle}
            keepFocus={keepFocus}
            animateHeight={animateHeight}
            capTop={capTop}
            head={head}
          >
            {children}
          </SheetBody>
        ) : null}
      </AnimatePresence>
    </>
  );
};

const PAD = 26;

/**
 * Where the sheet sits and how far its content clears the foot. Keyboard up: on
 * top of the keyboard (bottom = the overlap), 16 under the content. Keyboard
 * down: on the page foot, the design's 26 + what really covers the foot
 * (Safari's bar; the RESTING overlap) + the safe-area inset — SIGNUP_Anon §1:
 * a sheet clears the chrome, `padding:22px 20px 26px`.
 *
 * @param {{typing: boolean, overlap: number, resting: number}} at
 */
export const sheetPlace = ({ typing, overlap, resting }) =>
  typing
    ? { bottom: overlap, paddingBottom: 16 }
    : {
        bottom: 0,
        paddingBottom: `calc(${PAD + resting}px + env(safe-area-inset-bottom, 0px))`,
      };
const SHEET_IN = { duration: 0.3, ease: [0.22, 0.9, 0.24, 1] };
export const SHEET_OUT = { duration: 0.2, ease: [0.4, 0, 0.9, 0.4] };
// .cn-soft's cn-rise: 560ms, from 60% down, fading in over its first 40%
export const SOFT_IN = { duration: 0.56, ease: [0.16, 1, 0.3, 1] };

// The first tap on a text field: focus it from code without the page scroll
// (see Fields above). Prevented on pointerdown, so the native focus — and its
// scroll — never runs; the mousedown after it is prevented too while the field
// was not yet focused when the tap began.
const focusNoScroll = (e) => {
  const el = e.target;
  if (!isTextField(el) || document.activeElement === el) return;
  e.preventDefault();
  el.focus({ preventScroll: true });
};

// mousedown that would move focus off the field — keepFocus prevents it (a tap
// on a text field itself is let through so it can take focus)
const holdFocus = (e) => {
  if (!isTypingTarget(e.target)) e.preventDefault();
};

const SheetBody = ({
  title,
  description,
  onClose,
  children,
  soft,
  bigTitle,
  keepFocus,
  animateHeight,
  capTop,
  quiet,
  centred,
  head,
}) => {
  const rootRef = useRef(null);
  const overlap = useViewportOverlap(rootRef);
  const typing = useTypingFocus();
  // At rest the floor clears what covers the foot — the RESTING overlap, not the
  // live reading: for ~400 ms after a field lets go, visualViewport still reports
  // the leaving keyboard (Mark's v7974 recording: `typing n`, `ovl 377`), which
  // padded the sheet for a keyboard on its way out and dropped it in two steps.
  const resting = useRestingOverlap(overlap, typing);
  // #4: dismiss only when the tap STARTED on the dimmed page. Tapping a field
  // focuses it, the keyboard lifts the sheet mid-tap, and the release can land on
  // the dimmed page — which must NOT close it (proven on device: typing, 3–4ms
  // after focus). Tracked on pointerdown.
  const downOnScrim = useRef(false);
  // Leaving, the sheet keeps the place it had when it started to go: a field
  // let go at the same moment (Done, a submit) would otherwise drop it from the
  // keyboard's top to the page foot mid-exit, and take the keyboard fill away.
  const present = useIsPresent();
  const live = useRef({ typing, overlap, resting });
  if (present) live.current = { typing, overlap, resting };
  const at = live.current;
  const place = sheetPlace(at);
  return (
    <motion.div
      ref={rootRef}
      className={[styles.ov, soft && styles.soft, quiet && styles.quiet, centred && styles.centred]
        .filter(Boolean)
        .join(' ')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.22, ease: 'easeOut' } }}
      exit={{ opacity: 0, transition: { duration: 0.18, ease: 'easeIn' } }}
      onPointerDown={(e) => {
        downOnScrim.current = e.target === e.currentTarget;
        focusNoScroll(e);
      }}
      onMouseDown={(e) => {
        // Already focused by the pointerdown: let the mousedown be (caret);
        // not focused (no pointer events): the same focus from code
        focusNoScroll(e);
        if (keepFocus) holdFocus(e);
      }}
      onClick={(e) => {
        if (downOnScrim.current && e.target === e.currentTarget) onClose();
      }}
    >
      {at.typing && at.overlap > 0 ? (
        // The keyboard lifts the sheet to bottom: overlap; this runs the sheet
        // colour from its foot down through Safari's URL area to the keyboard,
        // so no page shows in the gap (detail-pass-2 #2). Tapping it is the sheet,
        // not the dimmed page, so it does not dismiss.
        <div
          className={styles.ovfill}
          style={{ height: at.overlap }}
          onClick={(e) => e.stopPropagation()}
          aria-hidden
        />
      ) : null}
      <motion.div
        className={styles.ovs}
        style={capTop ? { ...place, maxHeight: '75%' } : place}
        role="dialog"
        aria-label={title}
        initial={soft ? { y: '60%', opacity: 0 } : { y: '100%' }}
        animate={{ y: 0, opacity: 1, transition: soft ? SOFT_IN : SHEET_IN }}
        exit={{ y: '100%', transition: SHEET_OUT }}
        onClick={(e) => e.stopPropagation()}
      >
        {head}
        {title ? (
          <div className={bigTitle ? `${styles.ovt} ${styles.big}` : styles.ovt}>{title}</div>
        ) : null}
        {description ? <div className={styles.ovd}>{description}</div> : null}
        {animateHeight ? <GrowBox>{children}</GrowBox> : children}
      </motion.div>
    </motion.div>
  );
};

/**
 * The sheet's content, its height following what it holds in ONE movement on
 * the sheet's own curve (SHEET_IN's 300 ms and easing — the `.ovs` bottom
 * transition's values), so a sheet that grows inside one open sheet expands
 * instead of jumping. Measured, never assumed (ResizeObserver).
 */
const GROW_PAD = 16; // .grow's padding (room for shadows)
const GrowBox = ({ children }) => {
  const innerRef = useRef(null);
  const [h, setH] = useState(null);
  // Every new content re-reads the height before paint: a content swap (Name →
  // contact) starts the grow in the SAME frame the new content lands, so its
  // chips arrive with the height change, not a frame after the field (v7976 (g):
  // the ResizeObserver alone answers a frame late). Unchanged heights bail out.
  useLayoutEffect(() => {
    if (innerRef.current) setH(innerRef.current.offsetHeight);
  }, [children]);
  // …and the observer catches what changes size outside a render (fonts, images)
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className={styles.growBox} style={h == null ? undefined : { height: h + 2 * GROW_PAD }}>
      <div ref={innerRef}>{children}</div>
    </div>
  );
};

// A field that types text (focusNoScroll's targets): not a checkbox, radio, file,
// range or date control, and not disabled or read-only
const TEXT_TYPES = new Set(['', 'text', 'tel', 'email', 'url', 'search', 'number', 'password']);
const isTextField = (el) =>
  !!el &&
  !el.disabled &&
  !el.readOnly &&
  (el.tagName === 'TEXTAREA' ||
    (el.tagName === 'INPUT' && TEXT_TYPES.has((el.getAttribute('type') || '').toLowerCase())));

export default SheetOverlay;
